import { collection, addDoc, doc, getDoc, setDoc, deleteDoc, query, where, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { firestore, storage } from './firebase';
import { DatabaseSoundPackModel } from './DatabaseSoundPack';

// A teaser for an unreleased sound pack, shown in the library as "Coming <date>".
// Kept out of sample_packs so pack consumers never see it. Its cover is already
// the future pack's sample_packs/{id}/cover.png: the released pack takes over the
// teaser's id and cover, and a teaser whose id matches a visible pack is hidden.
export type DatabaseUpcomingPack = {
    id?: string; // Document ID from Firestore
    name: string;
    secondaryTitle?: string | null; // optional header subtitle (e.g. "Vol 1")
    coverImageUrl?: string | null;
    releaseDate: string; // ISO 8601
    public: boolean; // true = visible to all users, false = admins only
};

// Field name constants (kept private to this file)
class FieldPaths {
    static readonly NAME = "name";
    static readonly SECONDARY_TITLE = "secondaryTitle";
    static readonly COVER_IMAGE_URL = "coverImageUrl";
    static readonly RELEASE_DATE = "releaseDate";
    static readonly PUBLIC = "public";
}

export class DatabaseUpcomingPackModel {

    static readonly COLLECTION_NAME = "upcoming_packs";

    private static get _upcomingPacksCollection() {
        return collection(firestore, this.COLLECTION_NAME);
    }

    /** Comparator for sorting teasers soonest-release-first. */
    static sortByReleaseDateAsc(a: DatabaseUpcomingPack, b: DatabaseUpcomingPack): number {
        return a.releaseDate.localeCompare(b.releaseDate);
    }

    static fromFirestore(data: any, docId?: string): DatabaseUpcomingPack {
        if (
            typeof data !== "object" ||
            !data ||
            typeof data[FieldPaths.NAME] !== "string" ||
            typeof data[FieldPaths.RELEASE_DATE] !== "string" ||
            typeof data[FieldPaths.PUBLIC] !== "boolean"
        ) {
            throw new Error("Invalid upcoming pack document structure");
        }
        return {
            id: docId,
            name: data[FieldPaths.NAME],
            secondaryTitle: data[FieldPaths.SECONDARY_TITLE] || null,
            coverImageUrl: data[FieldPaths.COVER_IMAGE_URL] || null,
            releaseDate: data[FieldPaths.RELEASE_DATE],
            public: data[FieldPaths.PUBLIC],
        };
    }

    // Optional fields only when set — updateUpcomingPack replaces the whole
    // document, so a cleared optional field is removed.
    static toFirestore(pack: DatabaseUpcomingPack): any {
        const data: any = {
            [FieldPaths.NAME]: pack.name,
            [FieldPaths.RELEASE_DATE]: pack.releaseDate,
            [FieldPaths.PUBLIC]: pack.public,
        };
        if (pack.secondaryTitle) data[FieldPaths.SECONDARY_TITLE] = pack.secondaryTitle;
        if (pack.coverImageUrl) data[FieldPaths.COVER_IMAGE_URL] = pack.coverImageUrl;
        return data;
    }

    // ====================== ADMIN API METHODS ============================

    /**
     * Create an upcoming pack (the id field is ignored; Firestore assigns one).
     * @returns Promise<string> The document ID of the created teaser
     */
    static async createUpcomingPack(pack: DatabaseUpcomingPack): Promise<string> {
        const docRef = await addDoc(this._upcomingPacksCollection, this.toFirestore(pack));
        console.log(`Created upcoming pack "${pack.name}", id = ${docRef.id}`);
        return docRef.id;
    }

    /** Replace an existing upcoming pack (fields not in `pack` are removed). */
    static async updateUpcomingPack(packId: string, pack: DatabaseUpcomingPack): Promise<void> {
        await setDoc(doc(firestore, this.COLLECTION_NAME, packId), this.toFirestore(pack));
        console.log(`Updated upcoming pack ${packId}`);
    }

    /**
     * Upload (or replace) the teaser's cover as the future pack's cover.png and
     * return its download URL (store it in coverImageUrl).
     */
    static async uploadCover(packId: string, cover: File): Promise<string> {
        return await DatabaseSoundPackModel.uploadFile(packId, cover, 'cover.png');
    }

    /**
     * Delete a teaser document. Its cover goes too, unless the pack has been
     * uploaded under the teaser's id — then the cover is the pack's.
     */
    static async deleteUpcomingPack(packId: string): Promise<void> {
        const released = (await getDoc(doc(firestore, DatabaseSoundPackModel.COLLECTION_NAME, packId))).exists();
        if (!released) {
            const coverRef = ref(storage, `${DatabaseSoundPackModel.getPackFolderPath(packId)}/cover.png`);
            await deleteObject(coverRef).catch(error => {
                if (error?.code !== 'storage/object-not-found') throw error;
            });
        }
        await deleteDoc(doc(firestore, this.COLLECTION_NAME, packId));
        console.log(`Deleted upcoming pack ${packId}${released ? ' (cover kept for the released pack)' : ''}`);
    }

    /**
     * Subscribe to upcoming packs. With includePrivate=true, returns all of them
     * (public + private); admin Firestore rules required. With false, only
     * public ones. Returns an Unsubscribe.
     */
    static subscribeUpcomingPacks(
        includePrivate: boolean,
        onUpdate: (packs: DatabaseUpcomingPack[]) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        const q = includePrivate
            ? query(this._upcomingPacksCollection)
            : query(this._upcomingPacksCollection, where(FieldPaths.PUBLIC, '==', true));
        return onSnapshot(
            q,
            (snapshot) => {
                const packs: DatabaseUpcomingPack[] = [];
                snapshot.forEach((doc) => {
                    try {
                        packs.push(this.fromFirestore(doc.data(), doc.id));
                    } catch (error) {
                        console.error(`Error parsing upcoming pack ${doc.id}:`, error);
                    }
                });
                onUpdate(packs);
            },
            (error) => {
                console.error('Error in upcoming packs subscription:', error);
                onError?.(error);
            },
        );
    }
}
